import { useState } from 'react';
import { AppLayout } from '../../../components/layout';
import { Button, Input, Card, Alert } from '../../../components/core';
import { http } from '../../../lib/api';
import { useUiStore } from '../../../store/ui';

const NAV = [
  { active: 'dashboard', items: [{ to: '/dashboard', label: 'Dashboard', icon: '▦' }] },
  { active: 'streams', items: [{ to: '/streams', label: 'Streams', icon: '▶' }, { to: '/streams/test1/destinations', label: 'Destinations', icon: '🎯' }, { to: '/recordings', label: 'Recordings', icon: '⏺' }, { to: '/analytics', label: 'Analytics', icon: '📊' }] },
  {
    active: 'settings',
    section: 'Settings',
    items: [
      { to: '/settings/profile', label: 'Profile', icon: '👤' },
      { to: '/settings/security', label: 'Security', icon: '🔒' },
      { to: '/settings/preferences', label: 'Preferences', icon: '⚙' },
    ],
  },
  { active: 'settings', items: [{ to: '/billing', label: 'Billing', icon: '💳' }, { to: '/transactions', label: 'Transactions', icon: '🧾' }] },
];

export function SecurityPage() {
  const ui = useUiStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await http.post('/api/auth/password/change', { currentPassword, newPassword });
      setSuccess('Password changed successfully');
      ui.toast('success', 'Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="settings-page">
        <h1 className="page-title">Security</h1>
        <p className="text-muted mb-4">Manage your password and account security.</p>
        {error && <Alert kind="danger">{error}</Alert>}
        {success && <Alert kind="success">{success}</Alert>}
        <Card title="Change password">
          <form onSubmit={submit} className="stack">
            <Input label="Current password" type="password" value={currentPassword} onChange={setCurrentPassword} required autoComplete="current-password" />
            <Input label="New password" type="password" value={newPassword} onChange={setNewPassword} required autoComplete="new-password" hint="At least 8 characters" />
            <Input label="Confirm new password" type="password" value={confirmPassword} onChange={setConfirmPassword} required autoComplete="new-password" />
            <div className="mt-2">
              <Button type="submit" disabled={saving || !currentPassword || !newPassword || !confirmPassword}>
                {saving ? 'Changing…' : 'Change password'}
              </Button>
            </div>
          </form>
        </Card>
        <Card title="Active sessions" className="mt-4">
          <p className="text-muted text-sm">
            If you suspect unauthorized access, change your password immediately. This will revoke all existing sessions.
          </p>
        </Card>
      </div>
    </AppLayout>
  );
}
